/**
 * Overlap rules for the producten of a verzoek om wijziging (github zorgportaal#969).
 *
 * Keep in sync with `back-end/back-end/app/route/changereqs/vowSendValidation.ts`: this repo has no
 * shared package between front-end and back-end, and the back-end checks the same rules when sending.
 */
export interface VowOverlapProduct {
	/** 1 = ongewijzigd, 2 = gewijzigd, 3 = nieuw */
	soort: number | null
	/** `null` or 'Generiek' for a generiek product */
	categorie: string | null
	/** `null` or 'Aspecifiek' for an aspecifiek product */
	product: string | null
	begindatum: string | null
	ingangsdatum: string | null
	einddatum: string | null
}

/** Categories that may overlap, a client can have several of these at once */
const CATEGORIES_WITHOUT_OVERLAP_CHECK = ['11', '12', '13', '14'];
const OPEN_END = '4712-12-31';

const isGeneriek = (p: Pick<VowOverlapProduct, 'categorie'>) => p.categorie === null || p.categorie === 'Generiek';
const isAspecifiek = (p: Pick<VowOverlapProduct, 'product'>) => p.product === null || p.product === 'Aspecifiek';

/** The period a product is valid in, without einddatum it is open-ended */
export const vowProductPeriod = (p: Pick<VowOverlapProduct, 'begindatum' | 'ingangsdatum' | 'einddatum'>): {begin: string, end: string} => ({
	begin: p.begindatum || p.ingangsdatum || '',
	end: p.einddatum || OPEN_END,
});

/** Do both producten claim the same thing, regardless of their periods */
export const isSameVowSlot = (a: Pick<VowOverlapProduct, 'categorie' | 'product'>, b: Pick<VowOverlapProduct, 'categorie' | 'product'>): boolean => {
	if ([a.categorie, b.categorie].some(c => c !== null && CATEGORIES_WITHOUT_OVERLAP_CHECK.includes(c))) return false;
	if (isGeneriek(a) || isGeneriek(b)) return true;
	if (a.categorie !== b.categorie) return false;
	// within one categorie: a specific product only clashes with the same product or an aspecifiek one
	return isAspecifiek(a) || isAspecifiek(b) || a.product === b.product;
};

export const vowPeriodsOverlap = (a: Pick<VowOverlapProduct, 'begindatum' | 'ingangsdatum' | 'einddatum'>, b: Pick<VowOverlapProduct, 'begindatum' | 'ingangsdatum' | 'einddatum'>): boolean => {
	const pa = vowProductPeriod(a);
	const pb = vowProductPeriod(b);
	return pa.begin <= pb.end && pb.begin <= pa.end;
};

/** Do these two producten overlap: the same slot in overlapping periods */
export const vowProductsOverlap = (a: VowOverlapProduct, b: VowOverlapProduct): boolean =>
	isSameVowSlot(a, b) && vowPeriodsOverlap(a, b);

/**
 * The pairs of producten that overlap. Pairs of two ongewijzigde producten are skipped: they
 * are history the aanbieder is not changing with this verzoek.
 */
export const findVowOverlaps = <T extends VowOverlapProduct>(producten: T[]): [T, T][] => {
	const pairs: [T, T][] = [];
	producten.forEach((a, i) => {
		for (const b of producten.slice(i + 1)) {
			if (a.soort === 1 && b.soort === 1) continue;
			if (vowProductsOverlap(a, b)) pairs.push([a, b]);
		}
	});
	return pairs;
};
